import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

export interface DepotKpi {
  asOf: string;
  inYardContainers: number;
  pendingInspections: number;
  pendingMoves: number;
  unbilledCharges: number;
  gateTransactionsToday: number;
  pendingApprovals: number;
}

export interface DepotSite {
  id: string;
  depot_code: string;
  depot_name: string;
  timezone: string;
  address?: string | null;
  status: string;
  created_at?: string | null;
}

export interface DepotYardSlot {
  id: string;
  depot_id: string;
  depot_code?: string | null;
  depot_name?: string | null;
  slot_code: string;
  block_code: string;
  bay_no?: string | null;
  row_no?: string | null;
  tier_no?: string | null;
  slot_status: string;
  current_container_no?: string | null;
}

export interface DepotContainerVisit {
  id: string;
  depot_id: string;
  depot_code?: string | null;
  depot_name?: string | null;
  visit_ref: string;
  container_no: string;
  line_operator?: string | null;
  container_size_type?: string | null;
  load_status: string;
  visit_status: string;
  condition_status: string;
  hold_status: string;
  current_slot_code?: string | null;
  truck_plate?: string | null;
  gate_in_at?: string | null;
  gate_out_at?: string | null;
  last_event_at?: string | null;
}

export interface DepotGateTransaction {
  id: string;
  visit_id?: string | null;
  gate_txn_no: string;
  gate_type: string;
  transaction_status: string;
  container_no: string;
  truck_plate?: string | null;
  driver_name?: string | null;
  haulier_name?: string | null;
  release_ref?: string | null;
  rejection_reason?: string | null;
  performed_at: string;
  depot_code?: string | null;
  depot_name?: string | null;
}

export interface DepotAppointment {
  id: string;
  depot_id: string;
  visit_id?: string | null;
  appointment_no: string;
  appointment_type: 'gate_in' | 'gate_out';
  status: 'booked' | 'checked_in' | 'completed' | 'cancelled' | 'no_show';
  container_no?: string | null;
  line_operator?: string | null;
  container_size_type?: string | null;
  truck_plate?: string | null;
  driver_name?: string | null;
  haulier_name?: string | null;
  release_ref?: string | null;
  scheduled_start_at: string;
  scheduled_end_at: string;
  arrival_at?: string | null;
  departure_at?: string | null;
  note?: string | null;
  depot_code?: string | null;
  depot_name?: string | null;
}

export interface DepotEirRecord {
  id: string;
  visit_id?: string | null;
  eir_no: string;
  eir_type: string;
  container_no: string;
  condition_grade?: string | null;
  signed_by?: string | null;
  photo_count: number;
  signed_at: string;
}

export interface DepotInspection {
  id: string;
  visit_id?: string | null;
  inspection_no: string;
  container_no: string;
  inspection_type: string;
  condition_grade?: string | null;
  damage_status: string;
  damage_summary?: string | null;
  photo_count: number;
  inspected_by?: string | null;
  inspected_at: string;
}

export interface DepotMoveOrder {
  id: string;
  visit_id?: string | null;
  move_no: string;
  container_no: string;
  move_type: string;
  from_slot_code?: string | null;
  to_slot_code?: string | null;
  task_status: string;
  assigned_to?: string | null;
  equipment_ref?: string | null;
  requested_at: string;
  completed_at?: string | null;
}

export interface DepotChargeEvent {
  id: string;
  visit_id?: string | null;
  charge_no: string;
  container_no: string;
  charge_type: string;
  charge_status: string;
  currency: string;
  amount: string | number;
  event_ref?: string | null;
  event_at: string;
  dispute_reason?: string | null;
  depot_code?: string | null;
}

export interface DepotBillableCharge extends DepotChargeEvent {
  depot_name?: string | null;
  visit_ref?: string | null;
  line_operator?: string | null;
  load_status?: string | null;
  bill_to_party_id?: string | null;
  bill_to_name?: string | null;
  billing_entry_id?: string | null;
  billing_entry_number?: string | null;
  billing_entry_status?: string | null;
  billing_posted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DepotBillingRun {
  id: string;
  run_ref: string;
  run_date: string;
  run_mode: string;
  status: string;
  total_amount: string | number;
  item_count: number;
  group_count: number;
  first_entry_id?: string | null;
  rerun_of_run_id?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface DepotBillingRunItem {
  id: string;
  charge_event_id: string;
  charge_no?: string | null;
  bill_to_party_id?: string | null;
  bill_to_name?: string | null;
  currency: string;
  amount: string | number;
  ledger_entry_id?: string | null;
  ledger_entry_number?: string | null;
  ledger_entry_status?: string | null;
  item_status: string;
  error_message?: string | null;
  data?: Record<string, unknown> | string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface DepotBillingRunDetail {
  run: DepotBillingRun;
  items: DepotBillingRunItem[];
}

export interface DepotRepairEstimate {
  id: string;
  visit_id?: string | null;
  inspection_id?: string | null;
  estimate_no: string;
  container_no: string;
  estimate_status: string;
  approval_status: string;
  currency: string;
  labor_cost: string | number;
  material_cost: string | number;
  total_amount: string | number;
  damage_summary?: string | null;
  approval_required: boolean;
  approval_requested_at?: string | null;
  approved_at?: string | null;
  completed_at?: string | null;
  depot_code?: string | null;
}

export interface DepotRepairJob {
  id: string;
  visit_id?: string | null;
  repair_estimate_id?: string | null;
  repair_job_no: string;
  container_no: string;
  job_status: string;
  assigned_to?: string | null;
  repair_summary?: string | null;
  labor_hours: string | number;
  material_cost: string | number;
  started_at?: string | null;
  completed_at?: string | null;
  depot_code?: string | null;
}

export interface DepotWashJob {
  id: string;
  visit_id?: string | null;
  wash_job_no: string;
  container_no: string;
  wash_type: string;
  job_status: string;
  assigned_to?: string | null;
  detergent_type?: string | null;
  notes?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  depot_code?: string | null;
}

export interface DepotAuditEntry {
  id: string;
  actor_type: string;
  actor_id?: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  changes?: {
    fields?: string[];
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  } | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export interface DepotApproval {
  id: string;
  request_type: string;
  reviewer_role?: string | null;
  requester_role?: string | null;
  title: string;
  description?: string | null;
  priority: string;
  status: string;
  decided_by?: string | null;
  decision_reason?: string | null;
  decided_at?: string | null;
  payload?: Record<string, unknown> | string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface DepotAuditResponse {
  items: DepotAuditEntry[];
  approvals: DepotApproval[];
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatMoney(value: string | number, currency = 'SGD'): string {
  const amount = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function useDepotDashboard() {
  const [kpi, setKpi] = useState<DepotKpi | null>(null);
  const [recentGate, setRecentGate] = useState<DepotGateTransaction[]>([]);
  const [recentContainers, setRecentContainers] = useState<DepotContainerVisit[]>([]);
  const [pendingMoves, setPendingMoves] = useState<DepotMoveOrder[]>([]);
  const [unbilledCharges, setUnbilledCharges] = useState<DepotChargeEvent[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<DepotApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpiData, gateData, containerData, moveData, chargeData, approvalData] = await Promise.all([
        api.get<DepotKpi>('/depot/dashboard/kpi'),
        api.get<{ items: DepotGateTransaction[] }>('/depot/dashboard/recent-gate?limit=6'),
        api.get<{ items: DepotContainerVisit[] }>('/depot/dashboard/recent-containers?limit=6'),
        api.get<{ items: DepotMoveOrder[] }>('/depot/dashboard/pending-moves?limit=6'),
        api.get<{ items: DepotChargeEvent[] }>('/depot/charges/unbilled?limit=6'),
        api.get<{ items: DepotApproval[] }>('/depot/dashboard/pending-approvals?limit=6'),
      ]);
      setKpi(kpiData);
      setRecentGate(gateData.items ?? []);
      setRecentContainers(containerData.items ?? []);
      setPendingMoves(moveData.items ?? []);
      setUnbilledCharges(chargeData.items ?? []);
      setPendingApprovals(approvalData.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Depot dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { kpi, recentGate, recentContainers, pendingMoves, unbilledCharges, pendingApprovals, loading, error, refresh };
}

export function useDepotList<T>(path: string) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ items: T[] }>(path);
      setItems(data.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load list');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}
